import type { CalibrationProblem } from './instrumentalAnalysis';

// Reviewed calibration problems for the colorimetry and AAS modes. Moved out of
// instrumentalAnalysis.ts to keep both files under 1000 lines. Values are textbook-level
// model data (pending teaching review), not measured results.

const PERMANGANATE_EPSILON = 2.4e3;
const MMOL_PER_MOL = 1000;
const colorimetrySlope = (pathCm: number) => (PERMANGANATE_EPSILON * pathCm) / MMOL_PER_MOL;

const COLORIMETRY_SOURCES = [
  'Beer–Lambert law, A = εcl (IUPAC Gold Book: absorbance; molar absorption coefficient)',
  'Permanganate absorption maximum near 525 nm with ε ≈ 2.4 × 10³ L mol⁻¹ cm⁻¹ (standard analytical chemistry texts)',
];
const AAS_SOURCES = [
  'Element resonance lines: Cu 324.8 nm, Pb 283.3 nm, Na 589.0 nm, Zn 213.9 nm, Fe 248.3 nm, Ca 422.7 nm (NIST Atomic Spectra Database)',
  'Standard senior chemistry treatment of atomic absorption spectroscopy calibration',
];

interface ProblemEntry {
  id: string;
  title: string;
  context: string;
  standards: number[];
  sampleConcentration: number;
  dilutionFactor?: number;
}

function colorimetryProblem(entry: ProblemEntry & { pathCm: number }): CalibrationProblem {
  const { pathCm, ...rest } = entry;
  return {
    dilutionFactor: 1,
    ...rest,
    mode: 'colorimetry',
    analyte: 'MnO₄⁻',
    wavelengthNm: 525,
    unit: 'mmol L⁻¹',
    slopePerUnit: colorimetrySlope(pathCm),
    slopeNote: `Model ε = 2.4 × 10³ L mol⁻¹ cm⁻¹, path length l = ${pathCm.toFixed(2)} cm.`,
    noiseAbsorbance: 0.004,
    sources: COLORIMETRY_SOURCES,
    conditions: 'Aqueous solution at about 25 °C, blank-corrected, 525 nm (green filter).',
    reviewStatus: 'pending-teaching-review',
  };
}

const AAS_LINES = { Cu: 324.8, Pb: 283.3, Na: 589.0, Zn: 213.9, Fe: 248.3, Ca: 422.7 } as const;
/** Model instrument response, absorbance per mg L⁻¹. */
const AAS_MODEL_SLOPES = { Cu: 0.09, Pb: 0.02, Na: 0.4, Zn: 0.5, Fe: 0.06, Ca: 0.07 } as const;
const AAS_NOISE = { Cu: 0.003, Pb: 0.001, Na: 0.003, Zn: 0.003, Fe: 0.002, Ca: 0.003 } as const;

function aasProblem(entry: ProblemEntry & { element: keyof typeof AAS_LINES }): CalibrationProblem {
  const { element, ...rest } = entry;
  const wavelengthNm = AAS_LINES[element];
  return {
    dilutionFactor: 1,
    ...rest,
    mode: 'aas',
    analyte: element,
    wavelengthNm,
    unit: 'mg L⁻¹ (ppm)',
    slopePerUnit: AAS_MODEL_SLOPES[element],
    slopeNote:
      'Model instrument response. Real AAS sensitivity depends on the instrument and flame, so always calibrate.',
    noiseAbsorbance: AAS_NOISE[element],
    sources: AAS_SOURCES,
    conditions: `Air–acetylene flame AAS with a ${element} hollow-cathode lamp at ${wavelengthNm.toFixed(1)} nm, blank-corrected.`,
    reviewStatus: 'pending-teaching-review',
  };
}

const MNO4_STANDARDS = [0.04, 0.08, 0.12, 0.16, 0.2];

export const CALIBRATION_PROBLEMS: CalibrationProblem[] = [
  colorimetryProblem({
    id: 'col-mno4-basic', title: 'Permanganate in a stock solution', pathCm: 1,
    context: 'Find the permanganate concentration of a solution measured directly in a 1.00 cm cell.',
    standards: MNO4_STANDARDS, sampleConcentration: 0.132,
  }),
  colorimetryProblem({
    id: 'col-mno4-six', title: 'Six standards, narrow range', pathCm: 1,
    context: 'Six standards bracket a weak permanganate solution.',
    standards: [0.025, 0.05, 0.075, 0.1, 0.125, 0.15], sampleConcentration: 0.087,
  }),
  colorimetryProblem({
    id: 'col-mno4-long-cell', title: 'A longer cell', pathCm: 2,
    context: 'The same solution type is measured in a 2.00 cm cell. A longer path gives a steeper line.',
    standards: [0.02, 0.04, 0.06, 0.08, 0.1], sampleConcentration: 0.064,
  }),
  colorimetryProblem({
    id: 'col-mno4-diluted', title: 'A diluted sample', pathCm: 1,
    context:
      'The sample was diluted 1 in 5 (10.00 mL made up to 50.00 mL) before measurement. Report the original concentration.',
    standards: MNO4_STANDARDS, sampleConcentration: 0.55, dilutionFactor: 5,
  }),
  colorimetryProblem({
    id: 'col-mno4-too-strong', title: 'Too strong to read', pathCm: 1,
    context: 'An undiluted permanganate solution is measured against the usual standards.',
    standards: MNO4_STANDARDS, sampleConcentration: 0.31,
  }),
  colorimetryProblem({
    id: 'col-mno4-dilute-ten', title: 'A 1 in 10 dilution', pathCm: 1,
    context:
      'A concentrated permanganate solution was diluted 1 in 10 (5.00 mL made up to 50.00 mL) before measurement. Report the original concentration.',
    standards: MNO4_STANDARDS, sampleConcentration: 1.08, dilutionFactor: 10,
  }),
  colorimetryProblem({
    id: 'col-mno4-too-weak', title: 'Too pale to read', pathCm: 1,
    context: 'A very pale permanganate solution is measured against the usual standards.',
    standards: MNO4_STANDARDS, sampleConcentration: 0.012,
  }),
  colorimetryProblem({
    id: 'col-mno4-long-cell-diluted', title: 'Longer cell and a dilution', pathCm: 2,
    context:
      'The sample was diluted 1 in 2 (25.00 mL made up to 50.00 mL) and measured in a 2.00 cm cell. Report the original concentration.',
    standards: [0.02, 0.04, 0.06, 0.08, 0.1], sampleConcentration: 0.146, dilutionFactor: 2,
  }),
  aasProblem({
    id: 'aas-cu-water', element: 'Cu', title: 'Copper in a water sample',
    context: 'Measure copper in a filtered water sample.',
    standards: [1, 2, 3, 4, 5], sampleConcentration: 2.7,
  }),
  aasProblem({
    id: 'aas-cu-diluted', element: 'Cu', title: 'Copper in a plating rinse',
    context: 'The rinse water was diluted 1 in 10 before measurement. Report the original concentration.',
    standards: [1, 2, 3, 4, 5], sampleConcentration: 36, dilutionFactor: 10,
  }),
  aasProblem({
    id: 'aas-pb-extract', element: 'Pb', title: 'Lead in a soil extract',
    context: 'Measure lead in an acid extract of a soil sample.',
    standards: [2, 4, 6, 8, 10], sampleConcentration: 6.8,
  }),
  aasProblem({
    id: 'aas-pb-too-strong', element: 'Pb', title: 'Lead in industrial waste water',
    context: 'Measure lead in an undiluted waste-water sample.',
    standards: [2, 4, 6, 8, 10], sampleConcentration: 14,
  }),
  aasProblem({
    id: 'aas-na-drink', element: 'Na', title: 'Sodium in a diluted sports drink',
    context: 'Measure sodium in a diluted sports drink. The reading is already for the measured solution.',
    standards: [0.2, 0.4, 0.6, 0.8, 1.0], sampleConcentration: 0.57,
  }),
  aasProblem({
    id: 'aas-zn-tap-water', element: 'Zn', title: 'Zinc in tap water',
    context: 'Measure zinc in a tap-water sample that stood overnight in galvanised pipes.',
    standards: [0.2, 0.4, 0.6, 0.8, 1.0], sampleConcentration: 0.46,
  }),
  aasProblem({
    id: 'aas-fe-spinach', element: 'Fe', title: 'Iron in a spinach digest',
    context:
      'A spinach sample was digested in acid and the digest diluted 1 in 5 before measurement. Report the concentration in the undiluted digest.',
    standards: [1, 2, 3, 4, 5], sampleConcentration: 14, dilutionFactor: 5,
  }),
  aasProblem({
    id: 'aas-ca-hard-water', element: 'Ca', title: 'Calcium in hard water',
    context:
      'A hard-water sample was diluted 1 in 20 (5.00 mL made up to 100.0 mL) before measurement. Report the original concentration.',
    standards: [1, 2, 3, 4, 5], sampleConcentration: 62, dilutionFactor: 20,
  }),
  aasProblem({
    id: 'aas-pb-trace', element: 'Pb', title: 'Lead in drinking water',
    context:
      'Measure lead in a drinking-water sample using the usual flame AAS standards. Trace levels may sit below the lowest standard.',
    standards: [2, 4, 6, 8, 10], sampleConcentration: 0.9,
  }),
];
